import {
  Button,
  ComboBox,
  DataTable,
  DataTableSkeleton,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TextInput,
} from '@carbon/react';
import { Add } from '@carbon/icons-react';
import {
  asProblem,
  EmptyState,
  FormModal,
  hasPermission,
  problemMessage,
  useAdmin,
  useFormat,
  type RecordValues,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';
import { usePromotions, useVoucherBatches } from './rewardsApi';

type Kind = 'coupon' | 'gift_voucher';
type Choice = { id: string; text: string };
type Draft = {
  kind: Kind;
  promotion: Choice | null;
  batch: Choice | null;
  validDays: number | null;
  note: string;
  key: string;
};

async function loadRewards(accountId: string) {
  const { data, error } = await hoApi.GET('/api/v1/membership/accounts/{accountId}/rewards', {
    params: { path: { accountId } },
  });
  if (!data) throw error;
  return data;
}

/**
 * The coupons, gift vouchers and points given to a member (ADR 0079, FR-MEM-09), by hand or by a Rewards program, and issuing one by hand:
 * a new single-use code of an active promotion, or a free voucher of a batch, valid for some days if wanted. Codes are masked.
 */
export function MemberRewardsTab({ account }: { account: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canIssue = hasPermission(session, 'ho.members.accounts.manage');
  const accountId = String(account.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'member-rewards', accountId];
  const rewards = useQuery({ queryKey: key, queryFn: () => loadRewards(accountId) });
  const [draft, setDraft] = useState<Draft>();
  const [promotionSearch, setPromotionSearch] = useState('');
  const [batchSearch, setBatchSearch] = useState('');
  const promotions = usePromotions(promotionSearch);
  const batches = useVoucherBatches(batchSearch);

  const issue = useMutation({
    mutationFn: async (d: Draft) => {
      const { data, error } = await hoApi.POST('/api/v1/membership/accounts/{accountId}/rewards', {
        params: { path: { accountId } },
        body: {
          kind: d.kind,
          promotionId: d.kind === 'coupon' ? (d.promotion?.id ?? null) : null,
          voucherBatchId: d.kind === 'gift_voucher' ? (d.batch?.id ?? null) : null,
          validDays: d.validDays,
          note: d.note.trim() || null,
          idempotencyKey: d.key,
        },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: () => {
      setDraft(undefined);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const headers = [
    { key: 'issuedAt', header: t('loyalty.rewards.columns.issued') },
    { key: 'kind', header: t('loyalty.rewards.columns.kind') },
    { key: 'code', header: t('loyalty.rewards.columns.code') },
    { key: 'validTo', header: t('loyalty.rewards.columns.validTo') },
    { key: 'source', header: t('loyalty.rewards.columns.source') },
    { key: 'note', header: t('loyalty.rewards.columns.note') },
  ];

  if (rewards.isLoading)
    return (
      <DataTableSkeleton columnCount={headers.length} rowCount={5} headers={headers} aria-label={t('common.loading')} />
    );
  if (!rewards.data)
    return <ErrorNotice title={problemMessage(t, asProblem(rewards.error))} onRetry={() => void rewards.refetch()} />;

  const rows = rewards.data.map((r) => ({
    id: r.id,
    issuedAt: format.dateTime(r.issuedAt),
    kind:
      r.kind === 'points'
        ? `${t('loyalty.rewards.points')}: ${format.number(r.points ?? 0)}`
        : r.kind === 'coupon'
          ? t('loyalty.rewards.coupon')
          : t('loyalty.rewards.giftVoucher'),
    code: r.maskedCode,
    validTo: r.validTo ? format.dateTime(r.validTo) : t('loyalty.rewards.noEnd'),
    source: r.source === 'program' ? t('loyalty.rewards.byProgram') : t('loyalty.rewards.byHand'),
    note: r.note ?? '',
  }));
  const valid =
    !!draft &&
    (draft.kind === 'coupon' ? !!draft.promotion : !!draft.batch) &&
    (draft.validDays === null || draft.validDays >= 1);

  return (
    <div>
      <DataTable rows={rows} headers={headers}>
        {({ rows: tableRows, headers: tableHeaders, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer title={t('loyalty.rewards.title')} description={t('loyalty.rewards.hint')}>
            {canIssue && (
              <TableToolbar>
                <TableToolbarContent className="pos-table-toolbar__content">
                  <Button
                    renderIcon={Add}
                    onClick={() => {
                      issue.reset();
                      setDraft({
                        kind: 'coupon',
                        promotion: null,
                        batch: null,
                        validDays: 30,
                        note: '',
                        key: crypto.randomUUID(),
                      });
                    }}
                  >
                    {t('loyalty.rewards.issue')}
                  </Button>
                </TableToolbarContent>
              </TableToolbar>
            )}
            {tableRows.length === 0 ? (
              <EmptyState heading={t('loyalty.rewards.empty')} headingLevel={3} />
            ) : (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableHeader {...getHeaderProps({ header })} key={header.key}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow {...getRowProps({ row })} key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value as string}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </DataTable>
      {draft && (
        <FormModal
          title={t('loyalty.rewards.issue')}
          primaryText={t('loyalty.rewards.issueSave')}
          disabled={!valid}
          pending={issue.isPending}
          error={issue.error ? problemMessage(t, asProblem(issue.error)) : null}
          onClose={() => setDraft(undefined)}
          onSubmit={() => valid && issue.mutate(draft)}
        >
          <RadioButtonGroup
            legendText={t('loyalty.rewards.kind')}
            name="reward-kind"
            valueSelected={draft.kind}
            onChange={(value) => setDraft((d) => (d ? { ...d, kind: value as Kind } : d))}
          >
            <RadioButton id="reward-kind-coupon" labelText={t('loyalty.rewards.coupon')} value="coupon" />
            <RadioButton id="reward-kind-voucher" labelText={t('loyalty.rewards.giftVoucher')} value="gift_voucher" />
          </RadioButtonGroup>
          {draft.kind === 'coupon' ? (
            <ComboBox<Choice>
              id="reward-promotion"
              titleText={t('loyalty.rewards.promotion')}
              helperText={t('loyalty.rewards.promotionHelp')}
              items={promotions.data ?? []}
              itemToString={(item) => item?.text ?? ''}
              selectedItem={draft.promotion}
              onInputChange={(text) => setPromotionSearch(text)}
              onChange={({ selectedItem }) => setDraft((d) => (d ? { ...d, promotion: selectedItem ?? null } : d))}
            />
          ) : (
            <ComboBox<Choice>
              id="reward-batch"
              titleText={t('loyalty.rewards.batch')}
              helperText={t('loyalty.rewards.batchHelp')}
              items={batches.data ?? []}
              itemToString={(item) => item?.text ?? ''}
              selectedItem={draft.batch}
              onInputChange={(text) => setBatchSearch(text)}
              onChange={({ selectedItem }) => setDraft((d) => (d ? { ...d, batch: selectedItem ?? null } : d))}
            />
          )}
          <NumberInput
            id="reward-valid-days"
            label={t('loyalty.rewards.validDays')}
            helperText={t('loyalty.rewards.validDaysHelp')}
            value={draft.validDays ?? ''}
            min={1}
            max={3660}
            step={1}
            allowEmpty
            onChange={(_, { value }) =>
              setDraft((d) =>
                d ? { ...d, validDays: value === '' || value === undefined ? null : Number(value) || null } : d,
              )
            }
          />
          <TextInput
            id="reward-note"
            labelText={t('loyalty.rewards.note')}
            value={draft.note}
            maxLength={300}
            onChange={(e) => setDraft((d) => (d ? { ...d, note: e.target.value } : d))}
          />
        </FormModal>
      )}
    </div>
  );
}
